// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, FC, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue, IconButton, Svg, Asset, Button } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Roles } from '@/_shared/constants/roles';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints, useResponsiveStyle, useDialog, useNotification } from '@/_shared/hooks';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { useFavorite } from '@/training/contentsLikes/hooks/useFavorite';
import {
  getContentDetailsCall,
  getContentStartCall,
  postContentStartCall,
} from '@/training/hardSkills/state';
import { StartDocumentHeaderSkeleton } from './StartDocumentHeaderSkeleton';
import { StartDocumentSkeleton } from './StartDocumentSkeleton';
import { DialogStartDocumentPayload } from './types';

dayjs.extend(duration);

export const DialogStartDocument: FC = () => {
  const t = useTranslations('documents.startDialog');
  const tGeneric = useTranslations('trainingHardSkills.cards.GENERIC');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { closeDialog, Dialog, isOpen, payload } =
    useDialog<DialogStartDocumentPayload>('documentStarter');
  const navigate = useNavigate();

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const gapM = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`);
  const primaryColor = useThemeValue('colors', 'primary');
  const primaryBackgroundColor = useThemeValue('colors', 'primary/background');
  const {
    execute: executeGetContentDetails,
    loading: loadingGetContentDetails,
    error: errorGetContentDetails,
    data,
  } = getContentDetailsCall.hook();

  const { execute: executeGetContentStart, loading: loadingGetContentStart } =
    getContentStartCall.hook();

  const { execute: executePostContentStart, loading: loadingPostContentStart } =
    postContentStartCall.hook();

  const documentId = useMemo(() => payload?.documentId || '', [payload?.documentId]);
  const isSkillToBoost = useMemo(() => payload?.isSkillToBoost || false, [payload?.isSkillToBoost]);

  const {
    isFavorite: localIsFavorite,
    isLoading: isFavoriteLoading,
    toggleFavorite: onToggleFavorite,
  } = useFavorite({ contentId: documentId, initialIsFavorite: data?.userDetails?.favorite });

  const isDocumentInProgress = useMemo(
    () =>
      (data?.userDetails?.progress?.startedAt && !data?.userDetails?.progress?.completedAt) || '',
    [data?.userDetails?.progress?.completedAt, data?.userDetails?.progress?.startedAt],
  );

  const isDocumentCompleted = useMemo(
    () => !!data?.userDetails?.progress?.completedAt,
    [data?.userDetails?.progress?.completedAt],
  );

  const textButton = useMemo(() => {
    if (isDocumentInProgress) {
      return t('resume');
    }
    if (isDocumentCompleted) {
      return t('restart');
    }
    return t('start');
  }, [isDocumentInProgress, isDocumentCompleted, t]);

  const iconButton = useMemo(() => {
    if (isDocumentInProgress) {
      return 'eyeOn';
    }
    if (isDocumentCompleted) {
      return 'refresh';
    }
    return 'eyeOn';
  }, [isDocumentInProgress, isDocumentCompleted]);

  const executeToUse = useMemo(
    () => (isDocumentInProgress ? executeGetContentStart : executePostContentStart),
    [executeGetContentStart, executePostContentStart, isDocumentInProgress],
  );

  const loadingToUse = useMemo(
    () => (isDocumentInProgress ? loadingGetContentStart : loadingPostContentStart),
    [isDocumentInProgress, loadingGetContentStart, loadingPostContentStart],
  );

  const navigateToDocument = useCallback(
    (
      documentResponse:
        | ReturnType<typeof getContentStartCall.hook>['response']
        | ReturnType<typeof postContentStartCall.hook>['response'],
    ) => {
      if (documentResponse?.contentPath) {
        const startFrom =
          documentResponse?.progress?.type === ContentType.PDF
            ? documentResponse?.progress?.page || undefined
            : undefined;

        closeDialog();
        navigate(Routes.Document.replace(':documentId', documentId), {
          state: {
            documentUrl: documentResponse?.contentPath,
            title: data?.title,
            startFrom,
          },
        });
      }
    },
    [closeDialog, navigate, documentId, data?.title],
  );

  const buttonClickHandler = useCallback(() => {
    executeToUse({
      contentId: documentId,
      successSaga: ({ response }) => {
        navigateToDocument(response);
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeToUse, navigateToDocument, notify, tShared, documentId]);

  const DocumentInfoContent = useMemo(() => {
    return loadingGetContentDetails ? (
      <StartDocumentHeaderSkeleton />
    ) : (
      <Box
        display='flex'
        mx='l'
        flexDirection='column'
        px='s'
        width='100%'
        zIndex={99}
        justifyContent='flex-end'
        height='100%'
      >
        <Box mb='xs' textAlign='left'>
          <Typography variant='p2/light'>
            {data?.tags?.map(tag => tag.label.toLocaleUpperCase()).join(', ')}
          </Typography>
          <Typography variant='h1'>{data?.title}</Typography>
        </Box>
        <Box
          display='flex'
          flexWrap='wrap'
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'left',
          })}
          alignItems='stretch'
          style={{ gap: gapM }}
          marginRight={genericResponsiveValue({
            breakPoints: { mobile: '0' },
            defaultValue: 'auto',
          })}
          mb='l'
        >
          {/* DURATION */}
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Svg name='clock' fill='primary/text' size='xs' />
            <Typography variant='p2' ml='xxs' color='primary/text'>
              {t('duration', {
                time: dayjs.duration(data?.durationMinutes ?? 0, 'minutes').asMinutes(),
              })}
            </Typography>
          </Box>

          {/* RATING (se presenti) */}
          {data?.averageRating ? (
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name='star' stroke='primary/text' size='s' />
              <Typography variant='p2' ml='xxs' color='primary/text'>
                {data.averageRating.toFixed(1)}
              </Typography>
            </Box>
          ) : null}

          {/* CREDITS (se presenti) */}
          {data?.credits ? (
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name='wallet' stroke='primary/text' size='xs' />
              <Typography variant='p2' color='primary/text' ml='xxs'>
                {t('credits', { credits: data?.credits })}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }, [
    loadingGetContentDetails,
    data?.tags,
    data?.title,
    data?.durationMinutes,
    data?.averageRating,
    data?.credits,
    gapM,
    genericResponsiveValue,
    t,
  ]);

  const Header = useMemo(() => {
    return (
      <Box
        p='xs'
        display='flex'
        borderRadius='m'
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        alignItems='center'
        flexDirection='row'
        padding='none'
        pt='s'
        height={250}
        background={`linear-gradient(
          to bottom,
          ${primaryColor},
          transparent
        ),
        conic-gradient(from 0.725turn at 90% 35%, ${primaryColor} 1rad, ${primaryBackgroundColor} 4rad)`}
        position='relative'
      >
        {isDesktop ? DocumentInfoContent : null}
        <Box
          style={{ mixBlendMode: 'luminosity' }}
          marginLeft='auto'
          marginRight='xxl'
          marginTop='auto'
          display='flex'
        >
          <Asset width={225} domain='images' src={data?.thumbnailUrl} />
        </Box>
        <Box
          position='absolute'
          left={0}
          bottom={0}
          width='100%'
          height='100%'
          backgroundColor='primary'
          opacity={0.3}
        />
      </Box>
    );
  }, [primaryColor, primaryBackgroundColor, isDesktop, DocumentInfoContent, data?.thumbnailUrl]);

  useEffect(() => {
    if (documentId) {
      executeGetContentDetails({
        contentId: documentId,
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeGetContentDetails, tShared, documentId]);

  return (
    <Dialog
      header={Header}
      headerStyle={{ mt: 'none', mb: 'none' }}
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
      specialsComponentsInHeader={
        <>
          {/* Skill to boost badge - top left */}
          {!loadingGetContentDetails &&
            isSkillToBoost &&
            !isDocumentInProgress &&
            !isDocumentCompleted && (
              <Box
                ml={genericResponsiveValue({
                  breakPoints: {
                    desktop: 'm',
                    tablet: 'xxs',
                  },
                  defaultValue: 'none',
                })}
                px='xxs'
                py='xxxs'
                borderRadius='xl'
                alignItems='center'
                display='flex'
                flexDirection='row'
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <Svg fill='white' name='rocket' size='xs' />
                <Typography color='white' variant='p3/light' ml='xxxs' fontSize='xxs'>
                  {tGeneric('skillToBoost')}
                </Typography>
              </Box>
            )}
          {/* Favorite button - top right */}
          <Box display='flex' flex={1} width='100%' justifyContent='flex-end' mr='xxs'>
            {!loadingGetContentDetails ? (
              <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
                <Button
                  onClick={onToggleFavorite}
                  variant='positive'
                  spinnerColor='primary/text'
                  padding='none'
                  minWidth='auto'
                  size='m'
                  loading={isFavoriteLoading}
                  disabled={isFavoriteLoading}
                >
                  <Svg
                    name={localIsFavorite ? 'bookmark' : 'emptyBookmark'}
                    size='xs'
                    fill='primary'
                  />
                </Button>
              </ClaimsComponent>
            ) : null}
          </Box>
        </>
      }
    >
      {!isDesktop ? DocumentInfoContent : null}
      <Box display='flex' mx='l' flexDirection='column' px='s' width='100%'>
        {loadingGetContentDetails ? (
          <StartDocumentSkeleton />
        ) : (
          <>
            <Typography variant='p2/light'>{data?.description}</Typography>
            {/* line between row rowIndex and rowIndex+1 (not after the last one) */}
            {data && data?.views > 0 && (
              <Typography variant='p3/light' color='primary/text' mt='m'>
                {t(`colleaguesHint.${data?.views === 1 ? 'singular' : 'plural'}`, {
                  colleagues: data?.views || 0,
                })}
              </Typography>
            )}
          </>
        )}

        <IconButton
          variant='primary'
          onClick={buttonClickHandler}
          mt='l'
          mr='auto'
          data-testid='confirm-operation-button'
          loading={loadingToUse || loadingGetContentDetails}
          disabled={loadingToUse || loadingGetContentDetails || !!errorGetContentDetails}
          prefixSvg={iconButton}
          svgStyle={{
            size: 'xs',
          }}
          textStyle={{
            paddingLeft: 'xxs',
          }}
          spinnerColor='white'
        >
          {textButton}
        </IconButton>
      </Box>
    </Dialog>
  );
};
